// Per-project source-file filter for the streaming viewer.
//
// The project card's gear ("Model files") lets a user untick linked
// files or the host file; the choice is saved on the project
// (Project.modelSourceFilter.hiddenDocumentIds). This module fetches
// that setting once per project when a manifest is loaded and answers
// "skip this instance?" while sidecars hydrate, so the hidden files'
// fragments are never requested at all.
//
// An instance is attributed to a document two ways, first wins:
//   - the exporter tag on the sidecar row: `f` = index into
//     manifest.documents (or `doc` = the document id itself);
//   - the Revit episode prefix of its UniqueId (`u`): the server derives
//     each document's prefixes from the export's record trees (the same
//     files the Project Files tree shows) and sends the hidden ones as
//     hiddenPrefixes. On the rehearsal project this attributes every row.
// The same answer also carries hiddenTreePaths so the Project Files tree
// can drop the hidden documents.
import { getProjectModelDocuments } from "api/projects/modelSources";

const cache = new Map();          // projectId -> { setting, fetchedAt }
let active = null;                // { projectId, hidden:Set, hiddenPrefixes:Set, hiddenTreePaths:Set, documents:[] }

const EMPTY = () => ({ hidden: new Set(), hiddenPrefixes: new Set(), hiddenTreePaths: new Set() });
const treeBase = (p) => String(p || "").split("?")[0].split("/").pop();

export function projectIdFromManifestUrl(manifestUrl) {
  const m = String(manifestUrl || "").match(/\/uploads\/([0-9a-f]{24})\//i);
  return m ? m[1] : "";
}

async function fetchSetting(projectId) {
  const hit = cache.get(projectId);
  if (hit && Date.now() - hit.fetchedAt < 15000) return hit.setting;
  let setting = EMPTY();
  try {
    const r = await getProjectModelDocuments(projectId);
    setting = {
      hidden: new Set((r?.hiddenDocumentIds || []).map(String)),
      hiddenPrefixes: new Set((r?.hiddenPrefixes || []).map(String)),
      // switchedOff: files off by the gear OR the placement dialog's
      // visibility - the tree drops both (the rows are dropped by this
      // registry and documentTransformRegistry respectively).
      hiddenTreePaths: new Set((r?.switchedOff?.treePaths || r?.hiddenTreePaths || []).map(treeBase)),
    };
  } catch (e) {
    // No access / offline: show everything rather than nothing.
    setting = EMPTY();
  }
  cache.set(projectId, { setting, fetchedAt: Date.now() });
  return setting;
}

function withHelpers(state) {
  state.isTreePathHidden = (path) => state.hiddenTreePaths.size > 0 && state.hiddenTreePaths.has(treeBase(path));
  return state;
}

// Called by the streaming loader right after the manifest is parsed.
export async function activate(projectId, manifest) {
  const documents = Array.isArray(manifest?.documents) ? manifest.documents : [];
  const setting = projectId ? await fetchSetting(projectId) : EMPTY();
  active = withHelpers({ projectId, documents, ...setting });
  return active;
}

// For pages that build the Project Files tree before (or without) the
// streaming loader: the saved choice for a project, with
// isTreePathHidden(path) to drop hidden documents' record trees.
export async function ensure(projectId) {
  if (active && active.projectId === projectId) return active;
  const setting = projectId ? await fetchSetting(projectId) : EMPTY();
  return withHelpers({ projectId, documents: [], ...setting });
}

export function getActive() { return active; }

// Forget the cached choice after the modal saves so the next viewer
// open sees the new set.
export function invalidate(projectId) {
  if (projectId) cache.delete(projectId);
  else cache.clear();
}

// Document id for one sidecar instance row, or "" when the export did
// not tag it.
export function documentIdOf(inst, documents) {
  if (!inst) return "";
  if (inst.doc !== undefined && inst.doc !== null) return String(inst.doc);
  if (typeof inst.f === "number" && documents && documents[inst.f]) {
    const d = documents[inst.f];
    return String(d.id ?? d.hash ?? d.name ?? inst.f);
  }
  return "";
}

export function isInstanceHidden(inst) {
  if (!active || !inst) return false;
  // A tag names the row's file; a miss is not the last word, because the
  // gear's ids are record ids ("rec:...") whenever the server attributed
  // the files through the record trees, and then no manifest id matches.
  // Until 2026-09-18 a miss returned false here and a hidden file drew in
  // full on every tagged export. hiddenPrefixes never holds a prefix a
  // visible file also carries, so falling through cannot over-hide.
  if (active.hidden.size) {
    const id = documentIdOf(inst, active.documents);
    if (id && active.hidden.has(id)) return true;
  }
  if (active.hiddenPrefixes.size && typeof inst.u === "string" && inst.u.length >= 36) {
    return active.hiddenPrefixes.has(inst.u.slice(0, 36));
  }
  return false;
}
